import React, { useEffect, useState, useCallback } from "react";
import { fetchPersonById, updatePerson, updatePersonTracking, PersonTrackingPatch } from "../../../services/aro/personService";
import { PersonDetail, Snapshot } from "../../../types/aro";
import AROBadge       from "../shared/AROBadge/AROBadge";
import LoadingSpinner from "../shared/LoadingSpinner/LoadingSpinner";
import PageHeader     from "../../shared/PageHeader/PageHeader";
import Field          from "../../shared/Field/Field";
import Toast          from "../../shared/Toast/Toast";
import { certStatusBadge, canReactivate, isNoLinkedinPause, isTerminal, stopReasonLabel } from "../../../utils/certStatus";
import { formatDateLong, formatLabel, formatLevel } from "../../../utils/formatters";
import { useAuth } from "../../../context/useAuth";
import { canAccessQuarantine, canEditLinkedin } from "../../../utils/quarantineAccess";
import "./PersonDetailPage.css";

const KNOWN_STOP_REASONS = ["no_linkedin_url", "consecutive_failures", "window_expired", "manual_hold"];

interface Props {
  personId: string;
  onBack:   () => void;
  onSelectCertificate: (certificateId: number) => void;
}

interface PersonDraft {
  full_name:    string;
  linkedin_url: string;
}

// Tracking is one row per person (plan §6.7)
interface TrackingDraft {
  status:               "active" | "paused" | "stopped";
  status_reason:        string;
  status_reason_custom: string;
  tracking_end_date:    string;
  next_scrape_due:      string;
  baseline_company:     string;
  baseline_company_url: string;
}

function toTrackingDraft(p: PersonDetail): TrackingDraft | null {
  const t = p.tracking;
  if (!t) return null;
  const reason  = t.status_reason ?? "";
  const isKnown = KNOWN_STOP_REASONS.includes(reason);
  return {
    status:               t.status === "job_changed" ? "stopped" : t.status,
    status_reason:        reason && !isKnown ? "other" : reason,
    status_reason_custom: reason && !isKnown ? reason : "",
    tracking_end_date:    t.tracking_end_date ?? "",
    next_scrape_due:      t.next_scrape_due ?? "",
    baseline_company:     t.baseline_company ?? "",
    baseline_company_url: t.baseline_company_url ?? "",
  };
}

function nullIfBlank(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export default function PersonDetailPage({ personId, onBack, onSelectCertificate }: Props) {
  const { email: changedBy } = useAuth();
  const [person,    setPerson]    = useState<PersonDetail | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [toast,     setToast]     = useState<{ msg: string; variant: "success" | "info" | "error" } | null>(null);

  // Person edit
  const [isEditing,  setIsEditing]  = useState(false);
  const [draft,      setDraft]      = useState<PersonDraft | null>(null);
  const [saving,     setSaving]     = useState(false);
  const [saveError,  setSaveError]  = useState<string | null>(null);

  // Tracking edit
  const [isTrackingEditing, setIsTrackingEditing] = useState(false);
  const [trackingDraft,     setTrackingDraft]     = useState<TrackingDraft | null>(null);
  const [trackingSaving,    setTrackingSaving]    = useState(false);
  const [trackingError,     setTrackingError]     = useState<string | null>(null);
  const [reactivating,      setReactivating]      = useState(false);

  // Certificate expand (detail dropdown)
  const [expandedCertId, setExpandedCertId] = useState<number | null>(null);

  const toggleExpand = (id: number) =>
    setExpandedCertId((prev) => (prev === id ? null : id));

  const loadPerson = useCallback(() => {
    return fetchPersonById(personId)
      .then((p) => setPerson(p))
      .catch((e) => setError(e.message));
  }, [personId]);

  useEffect(() => {
    setLoading(true);
    loadPerson().finally(() => setLoading(false));
  }, [loadPerson]);

  // ── Person edit ───────────────────────────────────────────
  const startEdit = () => {
    if (!person) return;
    setDraft({ full_name: person.full_name ?? "", linkedin_url: person.linkedin_url ?? "" });
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => { setIsEditing(false); setDraft(null); setSaveError(null); };

  const handleSave = async () => {
    if (!person || !draft) return;
    const linkedinChanged = draft.linkedin_url.trim() !== (person.linkedin_url ?? "") && !!draft.linkedin_url.trim();

    setSaving(true);
    setSaveError(null);
    try {
      const fields: { full_name?: string; linkedin_url?: string } = {};
      if (canAccessQuarantine(changedBy) && draft.full_name.trim() !== (person.full_name ?? "")) fields.full_name   = draft.full_name.trim();
      if (draft.linkedin_url.trim() !== (person.linkedin_url ?? "")) fields.linkedin_url = draft.linkedin_url.trim();
      if (Object.keys(fields).length === 0) { setIsEditing(false); setDraft(null); return; }

      await updatePerson(person.id, fields, changedBy);
      await loadPerson();
      setIsEditing(false);
      setDraft(null);

      if (linkedinChanged) {
        setToast({ msg: "LinkedIn URL saved — paused tracking has been reactivated.", variant: "success" });
      } else {
        setToast({ msg: "Profile updated.", variant: "success" });
      }
    } catch (e: any) {
      setSaveError(e.message ?? "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  // ── Tracking reactivate (quick action) ────────────────────
  const handleReactivate = async () => {
    if (!person) return;
    setReactivating(true);
    try {
      await updatePersonTracking(person.id, { status: "active" }, changedBy);
      await loadPerson();
      setToast({ msg: "Tracking reactivated.", variant: "success" });
    } catch (e: any) {
      setToast({ msg: e.message ?? "Failed to reactivate.", variant: "error" });
    } finally {
      setReactivating(false);
    }
  };

  // ── Tracking edit ─────────────────────────────────────────
  const startTrackingEdit = () => {
    if (!person) return;
    setTrackingDraft(toTrackingDraft(person));
    setTrackingError(null);
    setIsTrackingEditing(true);
  };

  const cancelTrackingEdit = () => { setIsTrackingEditing(false); setTrackingDraft(null); setTrackingError(null); };

  const handleTrackingSave = async () => {
    if (!person?.tracking || !trackingDraft) return;
    const t = person.tracking;
    const reason = trackingDraft.status_reason === "other"
      ? trackingDraft.status_reason_custom.trim()
      : trackingDraft.status_reason;


    const fields: PersonTrackingPatch = {};
    if (trackingDraft.status !== t.status)                                    fields.status               = trackingDraft.status;
    if ((reason || null) !== t.status_reason && trackingDraft.status !== "active") fields.status_reason = reason || null;
    if (trackingDraft.tracking_end_date && trackingDraft.tracking_end_date !== t.tracking_end_date) fields.tracking_end_date = trackingDraft.tracking_end_date;
    if (trackingDraft.next_scrape_due && trackingDraft.next_scrape_due !== t.next_scrape_due)       fields.next_scrape_due   = trackingDraft.next_scrape_due;
    if (trackingDraft.baseline_company.trim()     !== (t.baseline_company ?? ""))     fields.baseline_company     = nullIfBlank(trackingDraft.baseline_company);
    if (trackingDraft.baseline_company_url.trim() !== (t.baseline_company_url ?? "")) fields.baseline_company_url = nullIfBlank(trackingDraft.baseline_company_url);
    if (Object.keys(fields).length === 0) { cancelTrackingEdit(); return; }

    setTrackingSaving(true);
    setTrackingError(null);
    try {
      await updatePersonTracking(person.id, fields, changedBy);
      await loadPerson();
      cancelTrackingEdit();
      setToast({ msg: fields.status === "active" ? "Tracking reactivated." : "Tracking updated.", variant: "success" });
    } catch (e: any) {
      setTrackingError(e.message ?? "Failed to save tracking.");
    } finally {
      setTrackingSaving(false);
    }
  };

  if (loading) return <div className="aro-page"><LoadingSpinner /></div>;
  if (error)   return <div className="aro-page"><div className="aro-error-banner">{error}</div></div>;
  if (!person) return null;

  const canEdit = canAccessQuarantine(changedBy);
  const canEditLi = canEditLinkedin(changedBy);
  const missingLinkedIn = !person.linkedin_url;
  const tracking        = person.tracking;
  const scrapeFailures  = tracking?.consecutive_failures ?? 0;
  const trackingFinal   = tracking ? isTerminal(tracking.status) : false;
  const certificates = person.certificates ?? [];
  const snapshots    = person.snapshots    ?? [];

  return (
    <div className="aro-page">
      <Toast
        message={toast?.msg ?? null}
        variant={toast?.variant}
        onClose={() => setToast(null)}
      />

      <button className="person-back-btn" onClick={onBack}>← Back to People</button>

      <PageHeader
        title={person.full_name || person.email || "Person Detail"}
        right={
          missingLinkedIn
            ? <span className="person-linkedin-warning">LinkedIn URL missing — tracking disabled</span>
            : undefined
        }
      />

      {/* ── Two-column layout ─────────────────────────────── */}
      <div className="person-layout">

        {/* Left: Profile (editable for allowlisted email only) */}
        <div className="person-layout__left">
          {canEditLi && isEditing && draft ? (
            <section className="person-card">
              <h2 className="person-card__title">Edit Profile</h2>
              <div className="person-edit-grid">
                <div className="person-edit-row person-edit-row--full">
                  <label className="person-edit-label">Full Name</label>
                  <input
                    className="person-edit-input"
                    value={draft.full_name}
                    disabled={!canEdit}
                    onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                  />
                </div>
                <div className="person-edit-row person-edit-row--full">
                  <label className="person-edit-label">
                    LinkedIn URL
                    {!draft.linkedin_url && (
                      <span className="person-required-hint"> — required for tracking</span>
                    )}
                  </label>
                  <input
                    className={`person-edit-input ${!draft.linkedin_url ? "person-edit-input--required" : ""}`}
                    value={draft.linkedin_url}
                    placeholder="https://linkedin.com/in/..."
                    onChange={(e) => setDraft({ ...draft, linkedin_url: e.target.value })}
                  />
                  {draft.linkedin_url && draft.linkedin_url !== (person.linkedin_url ?? "") && (
                    <span className="person-li-hint">Saving will reactivate paused tracking.</span>
                  )}
                </div>
              </div>
              {saveError && <p className="person-save-error">{saveError}</p>}
              <div className="person-card-actions">
                <button className="person-btn person-btn--ghost" onClick={cancelEdit} disabled={saving}>Cancel</button>
                <button className="person-btn person-btn--primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </section>
          ) : (
            <section className="person-card">
              <div className="person-card__header">
                <h2 className="person-card__title">Profile</h2>
                {canEditLi && <button className="person-edit-btn" onClick={startEdit}>Edit</button>}
              </div>
              <div className="person-field-grid">
                <Field label="Full Name" value={person.full_name} />
                <Field label="Email"     value={person.email} />
                <div className={missingLinkedIn ? "person-linkedin-field--missing" : ""}>
                  <Field label="LinkedIn URL" value={person.linkedin_url} link />
                  {missingLinkedIn && (
                    <span className="person-linkedin-warning-inline">Required for tracking</span>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Right: Details + the person's tracking */}
        <div className="person-layout__right">
          {canEdit && isTrackingEditing && trackingDraft ? (
            <section className="person-card">
              <h2 className="person-card__title">Edit Tracking</h2>
              <div className="person-edit-grid">
                <div className="person-edit-row">
                  <label className="person-edit-label">Tracking Status</label>
                  <select className="person-edit-select" value={trackingDraft.status}
                    onChange={(e) => setTrackingDraft({ ...trackingDraft, status: e.target.value as TrackingDraft["status"] })}>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="stopped">Stopped</option>
                  </select>
                </div>
                <div className="person-edit-row">
                  <label className="person-edit-label">
                    Status Reason
                    {trackingDraft.status !== "active" && <span className="person-required-hint"> — required</span>}
                  </label>
                  <select className="person-edit-select" value={trackingDraft.status_reason}
                    onChange={(e) => setTrackingDraft({ ...trackingDraft, status_reason: e.target.value, status_reason_custom: "" })}>
                    <option value="">None</option>
                    <option value="no_linkedin_url">No LinkedIn URL</option>
                    <option value="consecutive_failures">Consecutive Scrape Failures</option>
                    <option value="window_expired">Tracking Window Expired</option>
                    <option value="manual_hold">Manual Hold</option>
                    <option value="other">Other (specify below)</option>
                  </select>
                  {trackingDraft.status_reason === "other" && (
                    <input className="person-edit-input" placeholder="Enter reason…" value={trackingDraft.status_reason_custom}
                      onChange={(e) => setTrackingDraft({ ...trackingDraft, status_reason_custom: e.target.value })} />
                  )}
                </div>
                <div className="person-edit-row">
                  <label className="person-edit-label">Tracking End Date</label>
                  <input className="person-edit-input" type="date" min={new Date().toISOString().split("T")[0]}
                    value={trackingDraft.tracking_end_date}
                    onChange={(e) => setTrackingDraft({ ...trackingDraft, tracking_end_date: e.target.value })} />
                </div>
                <div className="person-edit-row">
                  <label className="person-edit-label">Next LinkedIn Check</label>
                  <input className="person-edit-input" type="date" value={trackingDraft.next_scrape_due}
                    onChange={(e) => setTrackingDraft({ ...trackingDraft, next_scrape_due: e.target.value })} />
                </div>
                <div className="person-edit-row">
                  <label className="person-edit-label">Company When Certified</label>
                  <input className="person-edit-input" value={trackingDraft.baseline_company}
                    onChange={(e) => setTrackingDraft({ ...trackingDraft, baseline_company: e.target.value })} />
                </div>
                <div className="person-edit-row">
                  <label className="person-edit-label">Company When Certified URL</label>
                  <input className="person-edit-input" placeholder="https://linkedin.com/company/..." value={trackingDraft.baseline_company_url}
                    onChange={(e) => setTrackingDraft({ ...trackingDraft, baseline_company_url: e.target.value })} />
                </div>
              </div>
              {trackingDraft.status === "active" && tracking?.status !== "active" && (
                <p className="person-cert-reactivate-note">
                  Setting status to Active will clear the reason and reset scrape failures.
                </p>
              )}
              {trackingError && <p className="person-save-error">{trackingError}</p>}
              <div className="person-card-actions">
                <button className="person-btn person-btn--ghost" onClick={cancelTrackingEdit} disabled={trackingSaving}>Cancel</button>
                <button className="person-btn person-btn--primary" onClick={handleTrackingSave} disabled={trackingSaving}>
                  {trackingSaving ? "Saving…" : "Save Tracking"}
                </button>
              </div>
            </section>
          ) : (
            <section className="person-card">
              <div className="person-card__header">
                <h2 className="person-card__title">Details</h2>
                {canEdit && tracking && !trackingFinal && (
                  <button className="person-edit-btn" onClick={startTrackingEdit}>Edit tracking</button>
                )}
              </div>
              <div className="person-field-grid">
                <Field label="Ingested Date"    value={formatDateLong(person.created_at)} />
                {person.current_company && (
                  <Field label="Current Company" value={person.current_company} />
                )}
                <Field label="Highest Level" value={formatLevel(person.highest_level)} />
                {tracking && (
                  <>
                    <div className="cert-expand-field">
                      <span className="cert-expand-label">Tracking Status</span>
                      {certStatusBadge(tracking.status, tracking.status_reason)}
                    </div>
                    <Field label="Status Reason" value={tracking.status_reason ? stopReasonLabel(tracking.status_reason) : null} />
                    <Field label="Company When Certified" value={tracking.baseline_company} />
                    <Field label="Tracking Window" value={`${formatDateLong(tracking.tracking_start_date)} – ${formatDateLong(tracking.tracking_end_date)}`} />
                    <Field label="Next LinkedIn Check" value={tracking.status === "active" ? formatDateLong(tracking.next_scrape_due) : null} />
                  </>
                )}
              </div>
              {scrapeFailures > 0 && (
                <div className={`person-failures-badge ${scrapeFailures >= 3 ? "person-failures-badge--critical" : ""}`}>
                  ⚠ {scrapeFailures} consecutive scrape failure{scrapeFailures !== 1 ? "s" : ""}
                  {scrapeFailures >= 3 && " — tracking paused"}
                </div>
              )}
              {tracking && (
                <div className="cert-actions-cell">
                  {canEdit && canReactivate(tracking.status, tracking.status_reason) && (
                    <button
                      className="person-btn person-btn--primary person-btn--sm"
                      onClick={handleReactivate}
                      disabled={reactivating}
                    >
                      {reactivating ? "…" : "Reactivate"}
                    </button>
                  )}
                  {canEditLi && isNoLinkedinPause(tracking.status, tracking.status_reason) && (
                    <button className="person-btn person-btn--ghost person-btn--sm" onClick={startEdit}>
                      + Add LinkedIn URL
                    </button>
                  )}
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* ── Certificates ─────────────────────────────────── */}
      <section className="person-card person-card--full">
        <h2 className="person-card__title">
          Certificates
          <span className="person-card__count">{certificates.length}</span>
        </h2>
        {certificates.length === 0 ? (
          <p className="aro-muted">No certificates.</p>
        ) : (
          <div className="person-cert-table-wrap">
            <table className="aro-table">
              <thead>
                <tr>
                  <th>Certificate Name</th>
                  <th>Level</th>
                  <th>Event Name</th>
                  <th>Certificate Earned Date</th>
                  <th>Actions</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((c, i) => {
                  const isExpanded = expandedCertId === c.id;
                  return (
                    <React.Fragment key={c.id}>
                      <tr className={i % 2 === 1 ? "aro-table__row--odd" : ""}>
                        <td className="aro-table__cell--primary">{c.certificate_name}</td>
                        <td>{formatLevel(c.level)}</td>
                        <td className="aro-muted">{c.event_name ?? "—"}</td>
                        <td>{formatDateLong(c.certificate_earned_date)}</td>
                        <td>
                          <div className="cert-actions-cell">
                            <button className="person-edit-btn" onClick={() => onSelectCertificate(c.id)}>
                              View
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            className={`cert-expand-btn ${isExpanded ? "cert-expand-btn--open" : ""}`}
                            onClick={() => toggleExpand(c.id)}
                            title={isExpanded ? "Collapse" : "Show details"}
                          >
                            ▼
                          </button>
                        </td>
                      </tr>

                      {/* Expanded detail row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="cert-expand-row">
                            <div className="cert-expand-panel">
                              <div className="cert-expand-grid">
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Credential ID</span>
                                  <span className="cert-expand-value">{c.credential_id}</span>
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Credential Link</span>
                                  {c.credential_url ? (
                                    <a className="aro-external-link" href={c.credential_url} target="_blank" rel="noreferrer">↗ View / verify</a>
                                  ) : (
                                    <span className="cert-expand-value">—</span>
                                  )}
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Issuer</span>
                                  <span className="cert-expand-value">{c.issuer_company_name}</span>
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">Company at the Time</span>
                                  <span className="cert-expand-value">{c.company_at_time || "—"}</span>
                                </div>
                                <div className="cert-expand-field">
                                  <span className="cert-expand-label">How Issued</span>
                                  <span className="cert-expand-value">{c.issue_method ? formatLabel(c.issue_method) : "—"}</span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Snapshot Timeline ─────────────────────────────── */}
      <section className="person-card person-card--full">
        <h2 className="person-card__title">Snapshot Timeline</h2>
        {snapshots.length === 0 ? (
          <p className="aro-muted">No snapshots yet.</p>
        ) : (
          <div className="aro-timeline">
            {snapshots.map((snap: Snapshot, idx) => {
              const prev    = idx > 0 ? snapshots[idx - 1] : null;
              const changed = prev && snap.company !== prev.company;
              return (
                <div
                  key={snap.id}
                  className={`aro-timeline__node ${changed ? "aro-timeline__node--changed" : ""}`}
                >
                  <div className="aro-timeline__dot" />
                  <div className="aro-timeline__content">
                    <span className="aro-timeline__date">{formatDateLong(snap.snapshot_date)}</span>
                    <span className="aro-timeline__company">
                      {snap.company || "—"}
                      {snap.company_linkedin_url && (
                        <a
                          className="aro-external-link"
                          href={snap.company_linkedin_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          ↗
                        </a>
                      )}
                    </span>
                    {changed && <AROBadge label="Company changed" variant="green" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ── Job Change (tracking is per person) ───────────── */}
      {tracking?.status === "job_changed" && (
        <section className="person-card person-card--full">
          <h2 className="person-card__title">Job Changes</h2>
          <div className="aro-job-changes">
            <div className="aro-job-change">
              <span className="aro-job-change__route">
                <span className="aro-job-change__old">
                  {tracking.baseline_company || "—"}
                  {tracking.baseline_company_url && (
                    <a className="aro-external-link" href={tracking.baseline_company_url} target="_blank" rel="noreferrer">↗</a>
                  )}
                </span>
                <span className="aro-job-change__arrow">→</span>
                <span className="aro-job-change__new">
                  {tracking.changed_company || "—"}
                  {tracking.changed_company_url && (
                    <a className="aro-external-link" href={tracking.changed_company_url} target="_blank" rel="noreferrer">↗</a>
                  )}
                </span>
              </span>
              <span className="aro-job-change__date">{formatDateLong(tracking.job_change_detected_at)}</span>
              <AROBadge label="Within Window" variant="green" />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
